import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Popconfirm, message, Modal, Timeline, Empty, Avatar, Descriptions, Divider, Steps, List, Form, Input, InputNumber, Select, Radio, Checkbox, Switch, Upload, Row, Col } from 'antd';
import { EyeOutlined, ReloadOutlined, CloseOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as workflowService from '../../services/workflowService';

const { TextArea } = Input;

// 表单字段接口
interface FormField {
  id: string;
  name: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  options?: { label: string; value: string }[];
  span?: number;
}

// 表单预览组件
const FormPreview: React.FC<{ formData: Record<string, any>; variables?: string }> = ({ formData, variables }) => {
  const [fields, setFields] = useState<FormField[]>([]);

  useEffect(() => {
    // 优先从variables中获取字段配置
    if (variables) {
      try {
        const vars = JSON.parse(variables);
        if (vars._formFields && Array.isArray(vars._formFields)) {
          setFields(vars._formFields);
          return;
        }
      } catch {}
    }
    // 如果没有字段配置，根据formData生成默认字段
    const defaultFields: FormField[] = Object.keys(formData).map(key => ({
      id: key,
      name: key,
      label: key,
      type: typeof formData[key] === 'boolean' ? 'switch' : typeof formData[key] === 'number' ? 'number' : 'input',
      span: 12,
    }));
    setFields(defaultFields);
  }, [formData, variables]);

  const renderFieldValue = (field: FormField, value: any) => {
    const commonStyle = { width: '100%' };
    switch (field.type) {
      case 'input':
        return <Input value={value} disabled style={commonStyle} />;
      case 'textarea':
        return <TextArea value={value} disabled rows={3} style={commonStyle} />;
      case 'number':
        return <InputNumber value={value} disabled style={commonStyle} />;
      case 'select':
        return <Select value={value} disabled style={commonStyle} options={field.options} />;
      case 'date':
        return <Input value={value} disabled style={commonStyle} />;
      case 'dateRange':
        return <Input value={Array.isArray(value) ? value.join(' ~ ') : value} disabled style={commonStyle} />;
      case 'radio':
        return <Radio.Group value={value} disabled options={field.options} />;
      case 'checkbox':
        return <Checkbox.Group value={Array.isArray(value) ? value : [value]} disabled options={field.options} />;
      case 'switch':
        return <Switch checked={value} disabled />;
      case 'file':
        return <Upload disabled><Button icon={<UploadOutlined />} disabled>已上传文件</Button></Upload>;
      default:
        return <Input value={String(value ?? '')} disabled style={commonStyle} />;
    }
  };

  if (fields.length === 0) {
    return <Empty description="暂无表单数据" />;
  }

  return (
    <Form layout="vertical" style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
      <Row gutter={16}>
        {fields.map(field => (
          <Col span={field.span || 12} key={field.id}>
            <Form.Item label={field.label} style={{ marginBottom: 12 }}>
              {renderFieldValue(field, formData[field.name])}
            </Form.Item>
          </Col>
        ))}
      </Row>
    </Form>
  );
};

const WorkflowInitiated: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [instances, setInstances] = useState<workflowService.WorkflowInstance[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [currentHistory, setCurrentHistory] = useState<workflowService.WorkflowHistory[]>([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentInstance, setCurrentInstance] = useState<workflowService.WorkflowInstance | null>(null);
  const [instanceTasks, setInstanceTasks] = useState<workflowService.WorkflowTask[]>([]);

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await workflowService.getMyInitiated({ page, pageSize: 10 });
      if (res.data) {
        setInstances(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (id: number) => {
    try {
      await workflowService.withdrawProcess(id);
      message.success('撤回成功');
      loadData();
    } catch {
      message.error('撤回失败');
    }
  };

  const handleCancel = async (id: number) => {
    try {
      await workflowService.cancelProcess(id);
      message.success('取消成功');
      loadData();
    } catch {
      message.error('取消失败');
    }
  };

  const viewDetail = async (instance: workflowService.WorkflowInstance) => {
    setCurrentInstance(instance);
    try {
      const [historyRes, tasksRes] = await Promise.all([
        workflowService.getInstanceHistory(instance.id),
        workflowService.getInstanceTasks(instance.id),
      ]);
      setCurrentHistory(historyRes.data as workflowService.WorkflowHistory[] || []);
      setInstanceTasks(tasksRes.data as workflowService.WorkflowTask[] || []);
      setDetailModalVisible(true);
    } catch {}
  };

  const columns: ColumnsType<workflowService.WorkflowInstance> = [
    {
      title: '流程标题',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record) => (
        <span style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => viewDetail(record)}>{title}</span>
      ),
    },
    { title: '流程类型', dataIndex: 'definitionName', key: 'definitionName' },
    {
      title: '当前节点',
      dataIndex: 'currentNodeName',
      key: 'currentNodeName',
      render: (name: string, record) => record.status === 1 ? <Tag color="processing">{name || '处理中'}</Tag> : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => {
        const info = workflowService.instanceStatusMap[status];
        return <Tag color={info?.color}>{info?.text}</Tag>;
      },
    },
    {
      title: '发起时间',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (time: string) => time?.substring(0, 16) || '-',
    },
    {
      title: '完成时间',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (time: string) => time?.substring(0, 16) || '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => viewDetail(record)}>详情</Button>
          {record.status === 1 && (
            <>
              <Popconfirm title="确定要撤回此流程吗？" onConfirm={() => handleWithdraw(record.id)}>
                <Button size="small" icon={<ReloadOutlined />}>撤回</Button>
              </Popconfirm>
              <Popconfirm title="确定要取消此流程吗？" onConfirm={() => handleCancel(record.id)}>
                <Button size="small" danger icon={<CloseOutlined />}>取消</Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="我发起的">
        <Table
          columns={columns}
          dataSource={instances}
          rowKey="id"
          loading={loading}
          pagination={{ current: page, total, pageSize: 10, showTotal: (t) => `共 ${t} 条`, onChange: setPage }}
        />
      </Card>

      <Modal title="审批历史" open={historyModalVisible} onCancel={() => setHistoryModalVisible(false)} footer={null} width={700}>
        {currentHistory.length > 0 ? (
          <Timeline
            items={currentHistory.map((h) => ({
              color: h.action === 'approve' ? 'green' : h.action === 'reject' ? 'red' : 'blue',
              children: (
                <div>
                  <Space><Avatar size="small">{h.operatorName?.charAt(0)}</Avatar><strong>{h.operatorName}</strong><Tag>{h.nodeName}</Tag></Space>
                  <div style={{ marginTop: 8, marginLeft: 32 }}>
                    <Tag color={h.action === 'approve' ? 'green' : h.action === 'reject' ? 'red' : 'blue'}>
                      {h.action === 'submit' && '发起流程'}{h.action === 'approve' && '审批通过'}{h.action === 'reject' && '审批拒绝'}
                    </Tag>
                    {h.comment && <div style={{ marginTop: 4, color: '#666', background: '#f5f5f5', padding: 8, borderRadius: 4 }}>{h.comment}</div>}
                  </div>
                </div>
              ),
            }))}
          />
        ) : <Empty description="暂无历史记录" />}
      </Modal>

      <Modal title="流程详情" open={detailModalVisible} onCancel={() => setDetailModalVisible(false)} footer={null} width={800}>
        {currentInstance && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="流程标题" span={2}>{currentInstance.title}</Descriptions.Item>
              <Descriptions.Item label="流程类型">{currentInstance.definitionName}</Descriptions.Item>
              <Descriptions.Item label="状态"><Tag color={workflowService.instanceStatusMap[currentInstance.status]?.color}>{workflowService.instanceStatusMap[currentInstance.status]?.text}</Tag></Descriptions.Item>
              <Descriptions.Item label="发起人">{currentInstance.initiatorName}</Descriptions.Item>
              <Descriptions.Item label="发起时间">{currentInstance.startTime?.substring(0, 16)}</Descriptions.Item>
            </Descriptions>
            {currentInstance.formData && (
              <>
                <Divider>表单数据</Divider>
                {(() => {
                  try {
                    const formData = JSON.parse(currentInstance.formData);
                    return <FormPreview formData={formData} variables={currentInstance.variables} />;
                  } catch { return <Empty description="表单数据解析失败" />; }
                })()}
              </>
            )}
            <Divider>流程进度</Divider>
            <Steps orientation="vertical" size="small" current={currentHistory.length - 1}
              items={currentHistory.map((h, index) => ({
                title: <Space><span>{h.nodeName}</span><Tag color={h.action === 'approve' ? 'green' : h.action === 'reject' ? 'red' : 'blue'}>{h.action === 'submit' ? '发起' : h.action === 'approve' ? '通过' : '拒绝'}</Tag></Space>,
                description: <div>{h.operatorName} - {h.createdAt?.substring(0, 16)}{h.comment && <div style={{ color: '#666' }}>意见: {h.comment}</div>}</div>,
                status: h.action === 'reject' ? 'error' : index === currentHistory.length - 1 && currentInstance.status === 1 ? 'process' : 'finish',
              }))}
            />
            {instanceTasks.length > 0 && (
              <>
                <Divider>任务列表</Divider>
                <List size="small" dataSource={instanceTasks} renderItem={(task) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar size="small">{task.assigneeName?.charAt(0)}</Avatar>}
                      title={<Space><span>{task.nodeName}</span><Tag color={workflowService.taskStatusMap[task.status]?.color}>{workflowService.taskStatusMap[task.status]?.text}</Tag></Space>}
                      description={<div>处理人: {task.assigneeName}</div>}
                    />
                  </List.Item>
                )} />
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WorkflowInitiated;
